import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Calendar, X } from 'lucide-react';

/**
 * Subtle sticky bar that appears after the user scrolls past the hero.
 * Keeps "Try Videra" + "Book a call" one tap away without dominating the page.
 * - Hides on /admin, /investors, /contact (page already IS the CTA)
 * - Hides on mobile once dismissed (localStorage)
 * - Only appears after scrolling > 700px
 */
const DISMISS_KEY = 'videra-sticky-cta-dismissed-v1';

const StickyCTABar = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage?.getItem(DISMISS_KEY)) {
        setDismissed(true);
      }
    } catch (e) { /* ignore storage errors */ }
  }, []);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 700);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const hiddenRoutes = ['/admin', '/investors', '/contact'];
  const shouldHide = hiddenRoutes.some((r) => location.pathname.startsWith(r));

  if (shouldHide || dismissed || !visible) return null;

  const dismiss = () => {
    setDismissed(true);
    try { window.localStorage?.setItem(DISMISS_KEY, '1'); } catch (e) { /* ignore */ }
  };

  return (
    <div
      className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 z-30 pointer-events-none"
      data-testid="v2-sticky-cta"
    >
      <div className="pointer-events-auto mx-auto md:mx-0 max-w-2xl md:max-w-none bg-slate-900/95 backdrop-blur-xl text-white rounded-2xl shadow-2xl border border-white/10 flex items-center gap-3 px-3 py-2 md:pl-5 md:pr-3 animate-[videra-sticky-in_.35s_ease-out]">
        <p className="hidden md:block text-sm font-medium text-white/90 pr-2">
          See Videra in your world.
        </p>
        <Link
          to="/?try=1"
          data-testid="v2-sticky-try"
          className="inline-flex items-center gap-1.5 bg-white text-slate-900 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-100 transition-colors"
        >
          Try Videra <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <Link
          to="/contact"
          data-testid="v2-sticky-book"
          className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-500 transition-colors"
        >
          <Calendar className="w-3.5 h-3.5" /> Book a call
        </Link>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="v2-sticky-dismiss"
          className="ml-1 w-7 h-7 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <style>{`@keyframes videra-sticky-in { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: translateY(0) } }`}</style>
    </div>
  );
};

export default StickyCTABar;
